import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    MenuItem,
    Grid,
} from '@mui/material';
import {
    validateFullName,
    validateIdNumber,
    validatePhoneNumber,
    validateEmployeeCode,
    validateDateOfBirth,
} from '../../utils/validation';
import LoadingSpinner from '../shared/LoadingSpinner';
import ErrorMessage from '../shared/ErrorMessage';

const StepVerifyInfo = ({ formData, setFormData, onSubmit, loading, error }) => {
    const [fieldErrors, setFieldErrors] = useState({});

    const handleChange = (field) => (event) => {
        setFormData({
            ...formData,
            [field]: event.target.value,
        });
        // Clear error when user starts typing
        if (fieldErrors[field]) {
            setFieldErrors({
                ...fieldErrors,
                [field]: '',
            });
        }
    };

    const handleBlur = (field, validator) => () => {
        if (!formData[field]) return;
        const result = validator(formData[field]);
        if (!result.valid) {
            setFieldErrors({
                ...fieldErrors,
                [field]: result.error,
            });
        }
    };

    const validateAllFields = () => {
        const errors = {};

        const validations = [
            { field: 'fullName', validator: validateFullName },
            { field: 'idNumber', validator: validateIdNumber },
            { field: 'phoneNumber', validator: validatePhoneNumber },
            { field: 'employeeCode', validator: validateEmployeeCode },
            { field: 'dateOfBirth', validator: validateDateOfBirth },
        ];

        validations.forEach(({ field, validator }) => {
            if (formData[field]) {
                const result = validator(formData[field]);
                if (!result.valid) {
                    errors[field] = result.error;
                }
            }
        });

        // Check required fields
        if (!formData.fullName) errors.fullName = 'Họ tên là bắt buộc';
        if (!formData.address) errors.address = 'Địa chỉ là bắt buộc';
        if (!formData.phoneNumber) errors.phoneNumber = 'Số điện thoại là bắt buộc';
        if (!formData.employeeCode) errors.employeeCode = 'Mã nhân viên là bắt buộc';

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = () => {
        if (validateAllFields()) {
            onSubmit();
        }
    };

    const isFormValid = formData.fullName && formData.address &&
        formData.phoneNumber && formData.employeeCode &&
        !Object.values(fieldErrors).some(e => e);

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Xác Nhận Thông Tin
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Kiểm tra và chỉnh sửa thông tin nếu cần. Các trường có dấu * là bắt buộc.
            </Typography>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Họ và Tên"
                        value={formData.fullName}
                        onChange={handleChange('fullName')}
                        onBlur={handleBlur('fullName', validateFullName)}
                        error={!!fieldErrors.fullName}
                        helperText={fieldErrors.fullName}
                        disabled={loading}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        label="Số CCCD/CMT"
                        value={formData.idNumber}
                        onChange={handleChange('idNumber')}
                        onBlur={handleBlur('idNumber', validateIdNumber)}
                        error={!!fieldErrors.idNumber}
                        helperText={fieldErrors.idNumber}
                        disabled={loading}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        required
                        label="Mã Nhân Viên"
                        value={formData.employeeCode}
                        onChange={handleChange('employeeCode')}
                        onBlur={handleBlur('employeeCode', validateEmployeeCode)}
                        error={!!fieldErrors.employeeCode}
                        helperText={fieldErrors.employeeCode}
                        disabled={loading}
                    />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Địa Chỉ"
                        value={formData.address}
                        onChange={handleChange('address')}
                        multiline
                        rows={2}
                        disabled={loading}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        label="Ngày Sinh"
                        value={formData.dateOfBirth}
                        onChange={handleChange('dateOfBirth')}
                        onBlur={handleBlur('dateOfBirth', validateDateOfBirth)}
                        error={!!fieldErrors.dateOfBirth}
                        helperText={fieldErrors.dateOfBirth || 'DD/MM/YYYY'}
                        placeholder="01/01/1990"
                        disabled={loading}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        select
                        label="Giới Tính"
                        value={formData.gender}
                        onChange={handleChange('gender')}
                        disabled={loading}
                    >
                        <MenuItem value="">Không chọn</MenuItem>
                        <MenuItem value="Nam">Nam</MenuItem>
                        <MenuItem value="Nữ">Nữ</MenuItem>
                    </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        required
                        label="Số Điện Thoại"
                        value={formData.phoneNumber}
                        onChange={handleChange('phoneNumber')}
                        onBlur={handleBlur('phoneNumber', validatePhoneNumber)}
                        error={!!fieldErrors.phoneNumber}
                        helperText={fieldErrors.phoneNumber}
                        placeholder="0901234567"
                        disabled={loading}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange('email')}
                        placeholder="email@example.com"
                        disabled={loading}
                    />
                </Grid>
            </Grid>

            <ErrorMessage error={error} />

            {loading && <LoadingSpinner message="Đang xử lý đăng ký..." />}

            {!loading && (
                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                    disabled={!isFormValid}
                    sx={{ mt: 3 }}
                >
                    Hoàn Tất Đăng Ký
                </Button>
            )}
        </Box>
    );
};

export default StepVerifyInfo;
